import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const SettingsPanel = ({ settings, onSave }) => {
  const [formData, setFormData] = useState({
    admin_email: '',
    commission_rate: 10,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        admin_email: settings.admin_email || '',
        commission_rate: settings.commission_rate || 10,
      });
    }
  }, [settings]);

  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/admin/system-status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
            },
          }
        );

        if (response.data.success) {
          setSystemStatus(response.data.systemStatus);
        }
      } catch (error) {
        setStatusError(error.response?.data?.message || 'Failed to load system status.');
      } finally {
        setStatusLoading(false);
      }
    };

    fetchSystemStatus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'commission_rate' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await onSave(formData);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setMessage(null);

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      setPasswordLoading(false);
      return;
    }

    try {
      await axios.put(
        `${API_BASE}/admin/password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          },
        }
      );

      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setMessage({ type: 'success', text: 'Password updated successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to update password.'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setTestEmailLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE}/admin/test-email`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          },
        }
      );

      setMessage({
        type: 'success',
        text: response.data.message || 'Test email sent successfully.'
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send test email.'
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl"
    >
      <div className="card p-8 bg-white/10 border border-white/20 backdrop-blur-xl">
        <h2 className="text-2xl font-bold text-white mb-8">Settings</h2>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-500/20 border-green-500/50 text-green-300'
                : 'bg-red-500/20 border-red-500/50 text-red-300'
            }`}
          >
            {message.text}
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Admin Email Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="space-y-3"
          >
            <label className="block text-white font-semibold mb-2">
              Admin Email Address
            </label>
            <p className="text-gray-400 text-sm mb-3">
              This is the email where order notifications will be sent
            </p>
            <input
              type="email"
              name="admin_email"
              value={formData.admin_email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/10 text-white border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
              placeholder="admin@ceylonbroker.com"
              required
            />
            <p className="text-gray-500 text-xs mt-2">
              ⚠️ Make sure this email is correct. All customer orders will be sent here.
            </p>
          </motion.div>

          {/* Commission Rate Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-3"
          >
            <label className="block text-white font-semibold mb-2">
              Commission Rate (%)
            </label>
            <p className="text-gray-400 text-sm mb-3">
              Percentage charged on each order (0-100%)
            </p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                name="commission_rate"
                min="0"
                max="100"
                step="0.5"
                value={formData.commission_rate}
                onChange={handleChange}
                className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
              />
              <div className="bg-blue-600 text-white font-bold px-6 py-3 rounded-lg min-w-24 text-center">
                {formData.commission_rate}%
              </div>
            </div>

            <div className="bg-blue-500/20 border border-blue-500/50 rounded p-4 mt-4">
                <p className="text-blue-300 text-sm">
                <strong>Example:</strong> If a customer buys $1000 worth of digital currency:
              </p>
              <p className="text-blue-300 text-sm mt-2">
                Commission: ${(1000 * formData.commission_rate / 100).toFixed(2)} ({formData.commission_rate}%)
              </p>
              <p className="text-blue-300 text-sm">
                Total charge: ${(1000 + 1000 * formData.commission_rate / 100).toFixed(2)}
              </p>
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={loading}
            type="submit"
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-4 text-lg font-bold"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </motion.button>
        </form>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-purple-500/20 border border-purple-500/50 rounded p-4"
        >
          <p className="text-purple-300 text-sm">
            <strong>📌 Note:</strong> Changes to these settings will apply to all future orders immediately.
            Existing orders will keep their original commission rate.
          </p>
        </motion.div>

        <div className="mt-10 pt-8 border-t border-white/10">
          <h3 className="text-xl font-bold text-white mb-2">Security</h3>
          <p className="text-gray-400 text-sm mb-6">
            Change the admin account password. Use a strong password and keep it private.
          </p>

          <form onSubmit={handlePasswordSubmit} className="space-y-5">
            <div>
              <label className="block text-white font-semibold mb-2">Current Password</label>
              <input
                type="password"
                name="current_password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 bg-white/10 text-white border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
                placeholder="Enter current password"
                required
              />
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">New Password</label>
              <input
                type="password"
                name="new_password"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 bg-white/10 text-white border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
                placeholder="Enter new password"
                minLength="8"
                required
              />
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">Confirm New Password</label>
              <input
                type="password"
                name="confirm_password"
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 bg-white/10 text-white border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
                placeholder="Confirm new password"
                minLength="8"
                required
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={passwordLoading}
              type="submit"
              className="w-full py-4 text-lg font-bold rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordLoading ? 'Updating Password...' : 'Change Password'}
            </motion.button>
          </form>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10">
          <h3 className="text-xl font-bold text-white mb-2">System Status</h3>
          <p className="text-gray-400 text-sm mb-6">
            Quick view of API, database, and email readiness.
          </p>

          {statusLoading ? (
            <div className="text-gray-300 text-sm">Loading status...</div>
          ) : statusError ? (
            <div className="text-red-300 text-sm">{statusError}</div>
          ) : systemStatus ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <p className="text-gray-400 text-sm">API</p>
                <p className="mt-1 text-green-300 font-semibold">{systemStatus.api}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <p className="text-gray-400 text-sm">Database</p>
                <p className="mt-1 text-green-300 font-semibold">{systemStatus.database}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <p className="text-gray-400 text-sm">Email</p>
                <p className={`mt-1 font-semibold ${systemStatus.email === 'configured' ? 'text-green-300' : 'text-amber-300'}`}>
                  {systemStatus.email === 'configured' ? 'Configured' : 'Missing config'}
                </p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <p className="text-gray-400 text-sm">Notification Email</p>
                <p className="mt-1 text-white font-semibold break-all">
                  {systemStatus.notificationEmail || 'Not set'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-amber-300 text-sm">System status unavailable.</div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSendTestEmail}
              disabled={testEmailLoading || statusLoading || !systemStatus}
              className="px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testEmailLoading ? 'Sending Test Email...' : 'Send Test Email'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPanel;
